import React, { useMemo, useState, useCallback } from 'react';
import { Table } from '@tanstack/table-core';
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';

import { SortOrder } from '../../../../enums/order';
import ColumnHeader from './column-header';
import DraggableColumnHeader from './draggable-column-header';

type IProps = {
  table: Table<any>;
  isLoading: boolean;
  sort: {
    sortBy: string;
    order: SortOrder;
  } | null;
  onSort?: ((sortBy: string, order: SortOrder) => void) | null;
  rows: any[];
  selectedRowIds: (string | number)[];
  onRowSelectAllHandler: (isSelect: boolean) => void;
  maxSelectedRowsLimit?: number | null;
  isSelectAllAcrossPages?: boolean;
  page?: number;
  pageSize?: number;
  appliedLimit?: number | null;
  enableCappedSelectAllUI?: boolean;
  allowIndivualRowDeSelectionAcrossAllPage?: boolean;
  onClearSelection?: (() => void) | null;
  isRowDisabled?: ((row: any) => boolean) | null;
  enableColumnOrdering?: boolean;
  pinnedColumns?: string[];
  onColumnOrderChange?: ((columnIds: string[]) => void) | null;
  defaultPinnedColumns?: string[];
  immovableColumns?: string[];
  onFreezeColumn?: ((columnId: string) => void) | null;
  onUnfreezeColumn?: ((columnId: string) => void) | null;
  enableRowSelection?: boolean;
};

const THead: React.FC<IProps> = ({
  table,
  isLoading,
  sort,
  onSort,
  rows,
  selectedRowIds,
  onRowSelectAllHandler,
  maxSelectedRowsLimit,
  isSelectAllAcrossPages = false,
  page = 1,
  pageSize = 0,
  appliedLimit = null,
  enableCappedSelectAllUI = false,
  allowIndivualRowDeSelectionAcrossAllPage = false,
  onClearSelection,
  isRowDisabled = null,
  enableColumnOrdering = false,
  pinnedColumns = [],
  onColumnOrderChange = null,
  defaultPinnedColumns = [],
  immovableColumns = [],
  onFreezeColumn = null,
  onUnfreezeColumn = null,
  enableRowSelection = true,
}) => {
  // Helper function to get current column IDs from table (always fresh, not memoized)
  const getCurrentColumnIds = () => {
    const headerGroup = table.getHeaderGroups()[0];
    if (!headerGroup) return [];
    return headerGroup.headers.map((h) => h.id);
  };

  // Combine pinned and immovable columns for movement restrictions
  const restrictedColumnsSet = useMemo(
    () => new Set([...pinnedColumns, ...immovableColumns]),
    [pinnedColumns, immovableColumns],
  );

  // Handle move column left
  const handleMoveLeft = useCallback(
    (columnId: string) => {
      if (!onColumnOrderChange) return;

      // Can't move if column itself is restricted
      if (restrictedColumnsSet.has(columnId)) return;

      // Get fresh column IDs directly from table
      const columnIds = getCurrentColumnIds();
      const currentIndex = columnIds.indexOf(columnId);
      if (currentIndex <= 1) return; // Can't move first column or checkbox column

      // Check if the column to the left is restricted (pinned or immovable)
      const leftColumnId = columnIds[currentIndex - 1];
      if (restrictedColumnsSet.has(leftColumnId)) return;

      const newOrder = [...columnIds];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [
        newOrder[currentIndex],
        newOrder[currentIndex - 1],
      ];
      onColumnOrderChange(newOrder);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onColumnOrderChange, restrictedColumnsSet, table],
  );

  // Handle move column right
  const handleMoveRight = useCallback(
    (columnId: string) => {
      if (!onColumnOrderChange) return;

      // Can't move if column itself is restricted
      if (restrictedColumnsSet.has(columnId)) return;

      // Get fresh column IDs directly from table
      const columnIds = getCurrentColumnIds();
      const currentIndex = columnIds.indexOf(columnId);
      if (currentIndex === -1 || currentIndex >= columnIds.length - 1) return;

      // Check if the column to the right is restricted (pinned or immovable)
      const rightColumnId = columnIds[currentIndex + 1];
      if (restrictedColumnsSet.has(rightColumnId)) return;

      const newOrder = [...columnIds];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [
        newOrder[currentIndex + 1],
        newOrder[currentIndex],
      ];
      onColumnOrderChange(newOrder);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onColumnOrderChange, restrictedColumnsSet, table],
  );

  // Get all column IDs for render calculations
  const allColumnIds = getCurrentColumnIds();
  // Memoize selection status calculation to prevent expensive recalculations
  const selectionStatus = useMemo(() => {
    // When select all across pages is active
    if (isSelectAllAcrossPages) {
      // If individual deselection is allowed, check actual selection state
      if (allowIndivualRowDeSelectionAcrossAllPage) {
        // Filter out disabled rows
        const selectableRows = isRowDisabled
          ? rows.filter((row) => !isRowDisabled(row))
          : rows;
        const selectableRowsIdsSet = new Set(
          selectableRows.map((row) => row.id),
        );
        const totalSelectableRows = selectableRowsIdsSet.size;

        // Count selected rows on current page
        const selectedCount = selectedRowIds.filter((id) =>
          selectableRowsIdsSet.has(id),
        ).length;

        if (selectedCount === 0) {
          return {
            isChecked: false,
            isIntermediate: false,
            isDisabled: false,
          };
        }

        if (selectedCount === totalSelectableRows) {
          return {
            isChecked: true,
            isIntermediate: false,
            isDisabled: false,
          };
        }

        return {
          isChecked: false,
          isIntermediate: true,
          isDisabled: false,
        };
      }

      // Limited select-all (credits/limit): only first N across pagination are "selected"
      if (
        enableCappedSelectAllUI &&
        appliedLimit != null &&
        appliedLimit > 0 &&
        pageSize > 0
      ) {
        const rowStartIndex = (page - 1) * pageSize;
        const selectableRows = isRowDisabled
          ? rows.filter((row) => !isRowDisabled(row))
          : rows;
        const totalSelectableRows = selectableRows.length;

        const selectedOnPage = selectableRows.filter((_, idx) => {
          const globalIndex = rowStartIndex + idx;
          return globalIndex < appliedLimit;
        }).length;

        if (selectedOnPage === 0) {
          return { isChecked: false, isIntermediate: false, isDisabled: false };
        }
        if (selectedOnPage === totalSelectableRows) {
          return { isChecked: true, isIntermediate: false, isDisabled: false };
        }
        return { isChecked: false, isIntermediate: true, isDisabled: false };
      }

      // Default unlimited: checkbox is checked when select all is active
      return { isChecked: true, isIntermediate: false, isDisabled: false };
    }

    // Filter out disabled rows
    const selectableRows = isRowDisabled
      ? rows.filter((row) => !isRowDisabled(row))
      : rows;
    const selectableRowsIdsSet = new Set(selectableRows.map((row) => row.id));
    const totalSelectableRows = selectableRowsIdsSet.size;

    // Count selected rows on current page using Set for O(1) lookup (only selectable ones)
    const selectedCount = selectedRowIds.filter((id) =>
      selectableRowsIdsSet.has(id),
    ).length;

    // Count unselected rows on current page (only selectable ones)
    const unselectedCount = totalSelectableRows - selectedCount;

    // Check if checkbox should be disabled
    // Disabled when: at max limit AND NOT all selected AND can't select more
    // This allows deselection when all rows are selected, even if at limit
    const isAtLimit =
      maxSelectedRowsLimit !== null &&
      selectedRowIds.length >= maxSelectedRowsLimit;
    const canSelectMore =
      maxSelectedRowsLimit === null ||
      selectedRowIds.length + unselectedCount <= maxSelectedRowsLimit;

    const isDisabled =
      isAtLimit && selectedCount < totalSelectableRows && !canSelectMore;

    if (selectedCount === 0) {
      return {
        isChecked: false,
        isIntermediate: false,
        isDisabled: isAtLimit && !canSelectMore,
      }; // No rows selected
    }

    if (selectedCount === totalSelectableRows) {
      return {
        isChecked: true,
        isIntermediate: false,
        isDisabled: false, // Always allow deselection when all rows are selected
      }; // All selectable rows selected
    }

    return {
      isChecked: false,
      isIntermediate: true,
      isDisabled,
    }; // Some rows selected
  }, [
    rows,
    selectedRowIds,
    maxSelectedRowsLimit,
    isSelectAllAcrossPages,
    page,
    pageSize,
    appliedLimit,
    enableCappedSelectAllUI,
    allowIndivualRowDeSelectionAcrossAllPage,
    isRowDisabled,
  ]);

  // Track which column's dropdown is open (null if none)
  const [openDropdownColumnId, setOpenDropdownColumnId] = useState<
    string | null
  >(null);

  // Callbacks for dropdown state management
  const handleDropdownOpen = useCallback((columnId: string) => {
    setOpenDropdownColumnId(columnId);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setOpenDropdownColumnId(null);
  }, []);

  // Memoize pinned columns set for O(1) lookup
  const pinnedColumnsSet = useMemo(() => new Set(pinnedColumns), [
    pinnedColumns,
  ]);

  // Memoize default pinned columns set
  const defaultPinnedColumnsSet = useMemo(() => new Set(defaultPinnedColumns), [
    defaultPinnedColumns,
  ]);

  // Get column IDs for sortable context (exclude first column and pinned columns)
  const sortableColumnIds = useMemo(() => {
    const headerGroup = table.getHeaderGroups()[0];
    if (!headerGroup) return [];

    return headerGroup.headers
      .filter((header, index) => {
        // Exclude first column (checkbox column) and pinned columns from sorting
        if (index === 0) return false;
        if (pinnedColumnsSet.has(header.id)) return false;
        return true;
      })
      .map((header) => header.id);
  }, [table, pinnedColumnsSet]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id || !onColumnOrderChange) {
      return;
    }

    const headerGroup = table.getHeaderGroups()[0];
    if (!headerGroup) return;

    const dragColumnIds = headerGroup.headers.map((h) => h.id);
    const activeIndex = dragColumnIds.indexOf(active.id as string);
    const overIndex = dragColumnIds.indexOf(over.id as string);

    if (activeIndex === -1 || overIndex === -1) return;

    // Don't allow dropping on pinned columns or first column
    if (overIndex === 0 || pinnedColumnsSet.has(over.id as string)) {
      return;
    }

    const newColumnOrder = arrayMove(dragColumnIds, activeIndex, overIndex);
    onColumnOrderChange(newColumnOrder);
  };

  const getRowSelectAllHandler = () => {
    // If individual deselection is allowed, use normal handler (deselects only current page)
    if (allowIndivualRowDeSelectionAcrossAllPage) {
      return onRowSelectAllHandler;
    }
    // Otherwise, when select all across pages is active, clicking checkbox clears all
    if (isSelectAllAcrossPages && onClearSelection) {
      return () => {
        onClearSelection();
      };
    }
    return onRowSelectAllHandler;
  };

  const renderHeaders = () =>
    table.getHeaderGroups().map((headerGroup) => (
      <div key={headerGroup.id} className="tanstack-table--tr">
        {headerGroup.headers.map((header, index) => {
          const isPinned = pinnedColumnsSet.has(header.id);

          if (enableColumnOrdering) {
            const isDefaultPinned = defaultPinnedColumnsSet.has(header.id);
            // A column is "frozen" if it's pinned but not a default pinned column
            const isFrozen = isPinned && !isDefaultPinned;

            // Calculate if column can move left/right
            // A column is restricted if it's pinned or immovable
            const isRestricted = restrictedColumnsSet.has(header.id);
            // Can't move left if: first column, second column (after checkbox), is restricted, or column to left is restricted
            const leftColumnId = index > 0 ? allColumnIds[index - 1] : null;
            const canMoveLeft =
              index > 1 &&
              !isRestricted &&
              !restrictedColumnsSet.has(leftColumnId || '');
            // Can't move right if: last column, is restricted, or column to right is restricted
            const rightColumnId =
              index < allColumnIds.length - 1 ? allColumnIds[index + 1] : null;
            const canMoveRight =
              index < allColumnIds.length - 1 &&
              !isRestricted &&
              !restrictedColumnsSet.has(rightColumnId || '');

            return (
              <DraggableColumnHeader
                key={header.id}
                table={table}
                header={header}
                headerIndex={index}
                sort={sort}
                onSort={onSort}
                checkboxState={selectionStatus}
                onRowSelectAllHandler={getRowSelectAllHandler()}
                isLoading={isLoading}
                enableRowSelection={enableRowSelection}
                isPinned={isPinned}
                enableDrag={enableColumnOrdering && !isLoading}
                isDefaultPinned={isDefaultPinned}
                isFrozen={isFrozen}
                onFreezeColumn={onFreezeColumn}
                onUnfreezeColumn={onUnfreezeColumn}
                onMoveLeft={handleMoveLeft}
                onMoveRight={handleMoveRight}
                canMoveLeft={canMoveLeft}
                canMoveRight={canMoveRight}
                isAnyDropdownOpen={openDropdownColumnId !== null}
                onDropdownOpen={handleDropdownOpen}
                onDropdownClose={handleDropdownClose}
              />
            );
          }

          return (
            <ColumnHeader
              key={header.id}
              table={table}
              header={header}
              headerIndex={index}
              sort={sort}
              onSort={onSort}
              checkboxState={selectionStatus}
              onRowSelectAllHandler={getRowSelectAllHandler()}
              isLoading={isLoading}
              enableRowSelection={enableRowSelection}
            />
          );
        })}
      </div>
    ));

  if (enableColumnOrdering && !isLoading) {
    return (
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
      >
        <div className="tanstack-table--thead tanstack-table--thead-dnd">
          <SortableContext
            items={sortableColumnIds}
            strategy={horizontalListSortingStrategy}
          >
            {renderHeaders()}
          </SortableContext>
        </div>
      </DndContext>
    );
  }

  return <div className="tanstack-table--thead">{renderHeaders()}</div>;
};

export default THead;
